import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import Chart from 'chart.js/auto';
import { ContractsService } from '../../../services/contracts.service';
import { formatRecentAmount, formatRecentWhen, mapRecents, RecentItem, recentStatusTone } from './portfolio-recent';

type MetricKey = 'frota' | 'agendamentos' | 'manutencoes' | 'orcamentos' | 'multas';

interface HealthTotals {
  frota: number;
  agendamentos: number;
  manutencoes: number;
  manutencoesAbertas: number;
  orcamentos: number;
  orcamentosPendentes: number;
  multas: number;
  multasPendentes: number;
}

interface HealthRow extends HealthTotals {
  contractId: number;
  contractNumber: string;
  contractName: string;
  status: string;
  agendamentosRecentes: RecentItem[];
  orcamentosRecentes: RecentItem[];
  sinistrosRecentes: RecentItem[];
  ordensRecentes: RecentItem[];
}

interface HealthPayload {
  periodDays: number;
  totals: HealthTotals;
  contracts: HealthRow[];
}

@Component({
  selector: 'app-b2b-portfolio-health',
  templateUrl: './b2b-portfolio-health.component.html',
  styleUrls: ['./b2b-portfolio-health.component.css']
})
export class B2bPortfolioHealthComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('compareChart') compareCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('mixChart') mixCanvas?: ElementRef<HTMLCanvasElement>;

  isLoading = false;
  error: string | null = null;
  periodDays = 30;
  recorte: 'todos' | 'pendentes' = 'todos';
  contractId: number | null = null;
  metric: MetricKey = 'agendamentos';
  payload: HealthPayload | null = null;

  readonly periods = [
    { label: '7 dias', value: 7 },
    { label: '30 dias', value: 30 },
    { label: '90 dias', value: 90 },
    { label: '6 meses', value: 180 },
    { label: '1 ano', value: 365 }
  ];

  readonly metrics: { key: MetricKey; label: string; color: string }[] = [
    { key: 'frota', label: 'Frota', color: '#1264E9' },
    { key: 'agendamentos', label: 'Agendamentos', color: '#6D28D9' },
    { key: 'manutencoes', label: 'Manutenções', color: '#B45309' },
    { key: 'orcamentos', label: 'Orçamentos', color: '#0369A1' },
    { key: 'multas', label: 'Multas', color: '#BE123C' }
  ];

  private compareChart: Chart<'bar'> | null = null;
  private mixChart: Chart<'doughnut'> | null = null;
  private chartPending = false;

  constructor(
    private contractsService: ContractsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewChecked(): void {
    if (!this.chartPending || !this.compareCanvas || !this.mixCanvas) return;
    this.chartPending = false;
    this.renderChart();
  }

  ngOnDestroy(): void {
    this.compareChart?.destroy();
    this.mixChart?.destroy();
  }

  get rows(): HealthRow[] {
    const all = this.payload?.contracts ?? [];
    if (this.contractId == null) return all;
    return all.filter(row => row.contractId === this.contractId);
  }

  get totals(): HealthPayload['totals'] {
    return this.rows.reduce((acc, row) => ({
      frota: acc.frota + row.frota,
      agendamentos: acc.agendamentos + row.agendamentos,
      manutencoes: acc.manutencoes + row.manutencoes,
      manutencoesAbertas: acc.manutencoesAbertas + row.manutencoesAbertas,
      orcamentos: acc.orcamentos + row.orcamentos,
      orcamentosPendentes: acc.orcamentosPendentes + row.orcamentosPendentes,
      multas: acc.multas + row.multas,
      multasPendentes: acc.multasPendentes + row.multasPendentes
    }), this.emptyTotals());
  }

  cardValue(key: MetricKey): number {
    const totals = this.totals;
    if (this.recorte === 'pendentes') {
      if (key === 'manutencoes') return totals.manutencoesAbertas;
      if (key === 'orcamentos') return totals.orcamentosPendentes;
      if (key === 'multas') return totals.multasPendentes;
    }
    return totals[key];
  }

  rowValue(row: HealthRow, key: MetricKey): number {
    if (this.recorte === 'pendentes') {
      if (key === 'manutencoes') return row.manutencoesAbertas;
      if (key === 'orcamentos') return row.orcamentosPendentes;
      if (key === 'multas') return row.multasPendentes;
    }
    return row[key];
  }

  cardHint(key: MetricKey): string {
    if (key === 'frota') return 'posição atual';
    if (this.recorte === 'pendentes' && key !== 'agendamentos') return 'pendentes no período';
    return `nos últimos ${this.periodDays} dias`;
  }

  selectMetric(key: MetricKey): void {
    this.metric = key;
    this.renderChart();
  }

  onContractChange(): void {
    this.renderChart();
  }

  load(): void {
    this.isLoading = true;
    this.error = null;
    this.contractsService.getB2bPortfolioHealth(this.periodDays).subscribe({
      next: (raw) => {
        this.payload = this.normalize(raw);
        this.isLoading = false;
        this.chartPending = true;
      },
      error: () => {
        this.isLoading = false;
        this.error = 'Não foi possível carregar a operação dos contratos.';
      }
    });
  }

  openContract(row: HealthRow): void {
    this.router.navigate(['/b2b/contracts', row.contractId, 'view']);
  }

  when(item: RecentItem): string {
    return formatRecentWhen(item.at, item.withTime);
  }

  money(item: RecentItem): string {
    return formatRecentAmount(item.amount);
  }

  tone(status: string): string {
    return recentStatusTone(status);
  }

  renderChart(): void {
    this.renderCompare();
    this.renderMix();
  }

  private renderCompare(): void {
    const canvas = this.compareCanvas?.nativeElement;
    if (!canvas) return;
    const ranked = this.chartRows();
    this.compareChart?.destroy();
    this.compareChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: ranked.map(row => row.contractName || row.contractNumber),
        datasets: this.metrics.map(metric => ({
          label: metric.label,
          data: ranked.map(row => this.rowValue(row, metric.key)),
          backgroundColor: metric.color,
          borderRadius: 4,
          maxBarThickness: 14
        }))
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { position: 'bottom' } },
        scales: {
          x: { beginAtZero: true, stacked: false, ticks: { precision: 0 } },
          y: { ticks: { color: '#334155' } }
        }
      }
    });
  }

  private renderMix(): void {
    const canvas = this.mixCanvas?.nativeElement;
    if (!canvas) return;
    this.mixChart?.destroy();
    this.mixChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: this.metrics.map(metric => metric.label),
        datasets: [{
          data: this.metrics.map(metric => this.cardValue(metric.key)),
          backgroundColor: this.metrics.map(metric => metric.color),
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: { legend: { position: 'bottom' } }
      }
    });
  }

  private chartRows(): HealthRow[] {
    if (this.contractId != null) return this.rows;
    return [...this.rows]
      .filter(row => this.metrics.some(metric => this.rowValue(row, metric.key) > 0))
      .sort((a, b) => this.rowScore(b) - this.rowScore(a))
      .slice(0, 10);
  }

  private rowScore(row: HealthRow): number {
    return this.metrics.reduce((sum, metric) => sum + this.rowValue(row, metric.key), 0);
  }

  private normalize(raw: any): HealthPayload {
    const source = raw?.contracts ?? raw?.Contracts ?? [];
    const contracts = (Array.isArray(source) ? source : []).map((item: any) => this.normalizeRow(item));
    const totals = raw?.totals ?? raw?.Totals ?? {};
    return {
      periodDays: Number(raw?.periodDays ?? raw?.PeriodDays ?? this.periodDays),
      totals: {
        frota: this.num(totals, 'frota', 'Frota'),
        agendamentos: this.num(totals, 'agendamentos', 'Agendamentos'),
        manutencoes: this.num(totals, 'manutencoes', 'Manutencoes'),
        manutencoesAbertas: this.num(totals, 'manutencoesAbertas', 'ManutencoesAbertas'),
        orcamentos: this.num(totals, 'orcamentos', 'Orcamentos'),
        orcamentosPendentes: this.num(totals, 'orcamentosPendentes', 'OrcamentosPendentes'),
        multas: this.num(totals, 'multas', 'Multas'),
        multasPendentes: this.num(totals, 'multasPendentes', 'MultasPendentes')
      },
      contracts
    };
  }

  private normalizeRow(item: any): HealthRow {
    return {
      contractId: this.num(item, 'contractId', 'ContractId'),
      contractNumber: String(item?.contractNumber ?? item?.ContractNumber ?? ''),
      contractName: String(item?.contractName ?? item?.ContractName ?? ''),
      status: String(item?.status ?? item?.Status ?? ''),
      frota: this.num(item, 'frota', 'Frota'),
      agendamentos: this.num(item, 'agendamentos', 'Agendamentos'),
      manutencoes: this.num(item, 'manutencoes', 'Manutencoes'),
      manutencoesAbertas: this.num(item, 'manutencoesAbertas', 'ManutencoesAbertas'),
      orcamentos: this.num(item, 'orcamentos', 'Orcamentos'),
      orcamentosPendentes: this.num(item, 'orcamentosPendentes', 'OrcamentosPendentes'),
      multas: this.num(item, 'multas', 'Multas'),
      multasPendentes: this.num(item, 'multasPendentes', 'MultasPendentes'),
      ...mapRecents(item)
    };
  }

  private num(source: any, camel: string, pascal: string): number {
    const value = source?.[camel] ?? source?.[pascal] ?? 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private emptyTotals(): HealthPayload['totals'] {
    return {
      frota: 0,
      agendamentos: 0,
      manutencoes: 0,
      manutencoesAbertas: 0,
      orcamentos: 0,
      orcamentosPendentes: 0,
      multas: 0,
      multasPendentes: 0
    };
  }
}
